import React, { useRef, useState, useEffect } from "react";
import { motion, AnimatePresence, useInView, useReducedMotion, animate } from "framer-motion";

const EASE = [0.22, 1, 0.36, 1];

// Fade + translateY + blur reveal when entering the viewport (once).
export function Reveal({ children, delay = 0, y = 12, className = "", once = true }) {
  const reduce = useReducedMotion();
  const ref = useRef(null);
  const inView = useInView(ref, { once, margin: "-60px" });
  const hidden = reduce ? { opacity: 0 } : { opacity: 0, y, filter: "blur(6px)" };
  const shown = reduce ? { opacity: 1 } : { opacity: 1, y: 0, filter: "blur(0px)" };
  return (
    <motion.div
      ref={ref}
      initial={hidden}
      animate={inView ? shown : hidden}
      transition={{ duration: reduce ? 0.2 : 0.5, delay, ease: EASE }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

// Reveal a phrase word by word. `highlight` = array of words rendered in accent color.
export function WordReveal({ text, className = "", stagger = 0.06, delay = 0, highlight = [] }) {
  const reduce = useReducedMotion();
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const words = String(text).split(" ");
  const isHi = (w) => highlight.includes(w.replace(/[.,!?]/g, ""));
  if (reduce) {
    return (
      <span ref={ref} className={className}>
        {words.map((w, i) => (
          <span key={i} className={isHi(w) ? "accent" : undefined}>{w}{i < words.length - 1 ? " " : ""}</span>
        ))}
      </span>
    );
  }
  return (
    <span ref={ref} className={className}>
      {words.map((w, i) => (
        <motion.span
          key={i}
          className={isHi(w) ? "accent" : undefined}
          style={{ display: "inline-block", whiteSpace: "pre" }}
          initial={{ opacity: 0, y: 10, filter: "blur(6px)" }}
          animate={inView ? { opacity: 1, y: 0, filter: "blur(0px)" } : {}}
          transition={{ duration: 0.5, delay: delay + i * stagger, ease: EASE }}
        >
          {w}{i < words.length - 1 ? " " : ""}
        </motion.span>
      ))}
    </span>
  );
}

// Count up from 0 to `to` when visible.
export function Counter({ to, duration = 1.2, className = "", format }) {
  const reduce = useReducedMotion();
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!inView) return;
    if (reduce) { setVal(to); return; }
    const controls = animate(0, to, { duration, ease: "easeOut", onUpdate: (v) => setVal(Math.floor(v)) });
    return controls.stop;
  }, [inView, to, reduce, duration]);
  const shown = format ? format(val) : val;
  return <span ref={ref} className={className}>{shown}</span>;
}

// Cycle through `items`, revealing each letter by letter, fading out upward.
export function RotatingText({ items, className = "", interval = 2500 }) {
  const reduce = useReducedMotion();
  const [i, setI] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setI((p) => (p + 1) % items.length), interval);
    return () => clearInterval(t);
  }, [items.length, interval]);
  const text = items[i];
  if (reduce) return <span className={className}>{text}</span>;
  return (
    <AnimatePresence mode="wait">
      <motion.span
        key={i}
        className={className}
        style={{ display: "inline-block" }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0, y: -10, filter: "blur(6px)" }}
        transition={{ duration: 0.3 }}
      >
        {text.split("").map((c, ci) => (
          <motion.span
            key={ci}
            style={{ display: "inline-block", whiteSpace: "pre" }}
            initial={{ opacity: 0, filter: "blur(6px)" }}
            animate={{ opacity: 1, filter: "blur(0px)" }}
            transition={{ duration: 0.3, delay: ci * 0.025 }}
          >
            {c}
          </motion.span>
        ))}
      </motion.span>
    </AnimatePresence>
  );
}

// Typewriter effect: writes `text` letter by letter, blinking cursor then gone.
export function Typewriter({ text, speed = 30, className = "", onDone }) {
  const reduce = useReducedMotion();
  const [n, setN] = useState(reduce ? text.length : 0);
  const [done, setDone] = useState(reduce);
  useEffect(() => {
    if (reduce) { onDone && onDone(); return; }
    let i = 0;
    const t = setInterval(() => {
      i += 1;
      setN(i);
      if (i >= text.length) {
        clearInterval(t);
        setTimeout(() => setDone(true), 700);
        onDone && onDone();
      }
    }, speed);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text]);
  return (
    <span className={className}>
      {text.slice(0, n)}
      {!done && <span className="tw-cursor" aria-hidden="true" />}
    </span>
  );
}
